import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, Search, Shield, Hexagon, X, Check, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Personnel({ user }) {
  const [personnel, setPersonnel] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    rank_id: '',
    role_ids: [],
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [personnelRes, ranksRes, rolesRes] = await Promise.all([
        axios.get(`${API}/personnel`),
        axios.get(`${API}/ranks`),
        axios.get(`${API}/roles`),
      ]);
      setPersonnel(personnelRes.data);
      setRanks(ranksRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError(error.response?.data?.detail || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePersonnel = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API}/personnel`, formData);
      setSuccess('Personnel created successfully!');
      setShowCreateModal(false);
      setFormData({ username: '', email: '', password: '', full_name: '', rank_id: '', role_ids: [] });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create personnel');
    }
  };

  const handleRoleToggle = (roleId) => {
    setFormData((prev) => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((id) => id !== roleId)
        : [...prev.role_ids, roleId],
    }));
  };

  const getRankBadgeClass = (rankName) => {
    const classes = {
      'Command': 'rank-command',
      'Commissioned Officer': 'rank-commissioned',
      'Director': 'rank-director',
      'Manager': 'rank-manager',
      'Warrant': 'rank-warrant',
      'Senior Operative': 'rank-senior-operative',
      'NCO': 'rank-nco',
      'Operative': 'rank-operative',
      'Enlisted': 'rank-enlisted',
      'Associate': 'rank-associate',
    };
    return classes[rankName] || 'rank-enlisted';
  };

  const getStatusClass = (status) => {
    const lower = status?.toLowerCase();
    if (lower === 'active') return 'status-active';
    if (lower === 'deployed') return 'status-deployed';
    if (lower === 'suspended') return 'status-suspended';
    if (lower === 'reserve') return 'status-reserve';
    return 'status-active';
  };

  const filteredPersonnel = personnel.filter(p => 
    p.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.service_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="spinner mb-4"></div>
        <p className="text-cyan-400 text-sm tracking-wider animate-pulse">LOADING PERSONNEL...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="personnel-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-white tracking-wide" style={{ fontFamily: 'ui-monospace, monospace', textShadow: '0 0 20px rgba(6, 182, 212, 0.4)' }}>
              PERSONNEL
            </h1>
            <span className="text-xs px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-400 font-mono border border-cyan-500/30">
              {filteredPersonnel.length} RECORDS
            </span>
          </div>
          <p className="text-slate-500 text-sm tracking-wider">CORPS PERSONNEL MANAGEMENT</p>
        </div>
        {user.rank?.level >= 3 && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="create-personnel-button"
          >
            <Plus size={16} />
            ADD PERSONNEL
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          placeholder="Search by name, username, or service number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input-tactical pl-12 w-full"
        />
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 animate-fade-in" data-testid="error-message">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 animate-fade-in" data-testid="success-message">
          <Check className="w-4 h-4 text-emerald-400" />
          <span className="text-emerald-400 text-sm">{success}</span>
        </div>
      )}

      {/* Personnel Table */}
      <div className="glass-panel overflow-hidden">
        <table className="tactical-table">
          <thead>
            <tr>
              <th>Service #</th>
              <th>Name</th>
              <th>Doctrine</th>
              <th>Rank</th>
              <th>Roles</th>
              <th>Status</th>
              <th>Department</th>
            </tr>
          </thead>
          <tbody>
            {filteredPersonnel.map((person) => (
              <tr key={person.id} data-testid={`personnel-row-${person.username}`}>
                <td className="mono">{person.service_number}</td>
                <td>
                  <div>
                    <div className="text-white font-medium">{person.full_name}</div>
                    <div className="text-xs text-slate-500">{person.email}</div>
                  </div>
                </td>
                <td>
                  {person.doctrine && (
                    <span className={`doctrine-badge ${person.doctrine?.code === 'GDT' ? 'doctrine-gdt' : 'doctrine-pist'}`} style={{ padding: '2px 8px', fontSize: '10px' }}>
                      <Hexagon size={10} />
                      {person.doctrine?.code}
                    </span>
                  )}
                </td>
                <td>
                  <span className={`rank-badge ${getRankBadgeClass(person.rank?.name)}`}>
                    {person.rank?.name}
                  </span>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {person.roles?.slice(0, 2).map((role) => (
                      <span key={role.id} className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
                        {role.name}
                      </span>
                    ))}
                    {person.roles?.length > 2 && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-500">
                        +{person.roles.length - 2}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <span className="flex items-center gap-2">
                    <span className={`status-indicator ${getStatusClass(person.status)}`}></span>
                    <span className="text-sm capitalize">{person.status?.replace('_', ' ')}</span>
                  </span>
                </td>
                <td className="text-slate-400">{person.department || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredPersonnel.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
            <p className="text-slate-500">No personnel found</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-white tracking-wider flex items-center gap-2">
                <Shield className="text-cyan-400" size={20} />
                ADD NEW PERSONNEL
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-500 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreatePersonnel} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Full Name</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="input-tactical"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="input-tactical"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-tactical"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-tactical"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Rank</label>
                <select
                  value={formData.rank_id}
                  onChange={(e) => setFormData({ ...formData, rank_id: e.target.value })}
                  className="input-tactical"
                  required
                >
                  <option value="">Select Rank</option>
                  {ranks.filter(r => r.level < user.rank?.level).map((rank) => (
                    <option key={rank.id} value={rank.id}>{rank.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">Roles</label>
                <div className="flex flex-wrap gap-2">
                  {roles.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => handleRoleToggle(role.id)}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                        formData.role_ids.includes(role.id)
                          ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
                          : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      {role.name}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-tactical">
                  CANCEL
                </button>
                <button type="submit" className="btn-primary">
                  CREATE PERSONNEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Personnel;
